/**
 * Transaction State Types
 *
 * Discriminated union type representing the canonical state of a transaction.
 * The state is derived from multiple boolean flags in TransactionV2 using
 * a priority-based derivation algorithm.
 *
 * Priority order (highest to lowest):
 * 1. Canceled - Transaction was canceled (irreversible)
 * 2. Settled - Money transferred to establishment
 * 3. Transferred - Money transferred to branch
 * 4. Paid - Payment successful
 * 5. Failed - Default state (not paid = failed)
 */

/**
 * Union type of all possible transaction state types
 */
export type TransactionStateType = 'Paid' | 'Transferred' | 'Settled' | 'Canceled' | 'Failed';

/**
 * Paid state - Payment was successfully completed
 */
export interface PaidState {
  readonly type: 'Paid';
  /** Date when the payment was made */
  readonly paidDate: Date | null;
  /** Whether a cancellation has been requested */
  readonly cancelRequested: boolean;
}

/**
 * Transferred state - Money was transferred to branch
 */
export interface TransferredState {
  readonly type: 'Transferred';
  /** Date when the transfer occurred */
  readonly transferDate: Date | null;
  /** Value transferred to branch */
  readonly value: number;
}

/**
 * Settled state - Money was paid to establishment
 */
export interface SettledState {
  readonly type: 'Settled';
  /** Date when the settlement occurred */
  readonly settledDate: Date | null;
  /** Value paid to establishment */
  readonly value: number;
}

/**
 * Canceled state - Transaction was canceled
 */
export interface CanceledState {
  readonly type: 'Canceled';
  /** Date when the cancellation occurred */
  readonly cancelDate: Date | null;
  /** Whether a cancellation was previously requested */
  readonly cancelRequested: boolean;
  /** Whether the transaction was paid before being canceled */
  readonly wasPaid: boolean;
}

/**
 * Failed state - Payment failed
 */
export interface FailedState {
  readonly type: 'Failed';
  /** Human-readable failure reason */
  readonly reason: string;
  /** Return code from payment processor */
  readonly returnCode: string;
}

/**
 * Discriminated union of all transaction states
 *
 * Use this type with exhaustive switch statements to ensure
 * all states are handled. TypeScript will error at compile time
 * if a state is not handled.
 *
 * @example
 * ```typescript
 * function handleState(state: TransactionState): void {
 *   switch (state.type) {
 *     case 'Paid':
 *       console.log('Paid on:', state.paidDate);
 *       break;
 *     // ... handle all other states
 *     default:
 *       const _exhaustive: never = state;
 *       throw new Error(`Unhandled state: ${_exhaustive}`);
 *   }
 * }
 * ```
 */
export type TransactionState =
  | PaidState
  | TransferredState
  | SettledState
  | CanceledState
  | FailedState;
